import { Link, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { BarChart3, Calendar, Home, User, X, TrendingUp, Scale, Wrench, Wheat } from 'lucide-react'

interface SidebarProps {
  isOpen?: boolean
  onToggle?: () => void
}

const navigation = [
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Statistics', href: '/statistics', icon: BarChart3 },
  { name: 'Calendar', href: '/calendar', icon: Calendar },
  { name: 'Weekly Charts', href: '/weekly-charts', icon: TrendingUp },
  { name: 'Fiber', href: '/fiber', icon: Wheat },
  { name: 'Body Measurements', href: '/body-measurements', icon: Scale },
  { name: 'Utils', href: '/utils', icon: Wrench },
  { name: 'Profile', href: '/profile', icon: User },
]

export function Sidebar({ isOpen = false, onToggle }: SidebarProps) {
  const location = useLocation()

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-background/75 lg:hidden"
          onClick={onToggle}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <nav
        className={cn(
          'fixed top-0 left-0 z-50 h-full w-64 bg-card border-r border-border transform transition-transform duration-300 ease-in-out lg:static lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
        role="navigation"
      >
        <div className="flex h-full flex-col">
          {/* Mobile close button */}
          <div className="flex h-16 items-center justify-between px-4 lg:hidden">
            <span className="text-lg font-semibold text-card-foreground">Menu</span>
            <Button variant="ghost" size="icon" onClick={onToggle} aria-label="Close sidebar">
              <X className="size-5" />
            </Button>
          </div>

          {/* Navigation links */}
          <div className="flex-1 px-4 py-4">
            <ul className="space-y-2">
              {navigation.map((item) => {
                const Icon = item.icon
                const isActive = location.pathname === item.href

                return (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className={cn(
                        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      )}
                      onClick={() => {
                        // Close mobile menu when link is clicked
                        if (window.innerWidth < 1024) {
                          onToggle?.()
                        }
                      }}
                    >
                      <Icon className="size-5" />
                      {item.name}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
      </nav>
    </>
  )
}
